import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useStellarApiClient } from "@hooks/use-stellar-api-client";
import { useToast } from "@flat-ui";
import { runtimeConfig } from "@src/runtime-config";
import { sentryCaptureError } from "@src/utils/sentry-utils";
import { EmailUtils } from "@stellar/utils";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import { defaultProjectLaunchTargetSelector, selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { getFeedbackMessage } from "@utils/email-utils";
import { FeedbackModalEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
/**
 * Hook to send feedback event to the backend and to track them.
 * Use the functions here instead of calling the API directly,
 * since some values are automatically filled in.
 */
export function useFeedback() {
  const selectedCompany = useAppSelector(selectedSdbCompanySelector);
  const selectedProject = useAppSelector(selectedProjectSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const {
    defaultOpenProjectTarget
  } = useAppSelector(defaultProjectLaunchTargetSelector);
  const {
    openToast
  } = useToast();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    stellarApiClient
  } = useStellarApiClient();

  /**
   * Sends a request to the backend to send a feedback email, and to track the event in Amplitude.
   * This function handles the error and shows a toast if the request fails, therefore does not
   * have to use try/catch since it will always resolve the promise.
   */
  async function sendFeedback({
    featureName,
    feedbackMessage,
    selectedRating,
    hasUserAllowedContact = false
  }) {
    const isInternalUser = EmailUtils.isInternalUser(currentUser?.email ?? "");
    const customMessage = getFeedbackMessage({
      selectedRating,
      feedbackMessage,
      defaultOpenProjectTarget,
      hasUserAllowedContact,
      isInternalUser
    });
    const subject = `SDB Feedback: ${featureName}`;
    trackEvent({
      name: FeedbackModalEvents.submitFeedback,
      props: {
        topic: subject,
        rating: selectedRating?.toString() ?? "",
        didProvideFeedback: Boolean(feedbackMessage),
        defaultOpenProjectTarget,
        hasUserAllowedContact: hasUserAllowedContact.toString()
      }
    });
    async function sendFeedbackToFeedbackAPI() {
      try {
        await stellarApiClient.feedback.submitFeedback({
          application: "SXG Dashboard",
          appVersion: runtimeConfig.appVersion,
          feedbackMessage: feedbackMessage ?? "",
          featureName,
          selectedRating: selectedRating ?? undefined,
          companyId: selectedCompany?.id,
          userId: currentUser?.id,
          workspaceRole: selectedCompany?.role.company,
          defaultOpenProjectTarget,
          hasUserAllowedContact,
          isInternalUser
        });
      } catch (error) {
        // Failing to call the feedback route should not block the user from using the app
        // Use Sentry to log the error
        sentryCaptureError({
          error,
          title: "Error calling feedback route"
        });
      }
    }
    async function sendFeedbackToCoreAPI() {
      try {
        await faroCoreApiClient.notification.notificationRequest({
          payload: {
            type: "feedback",
            context: {
              companyId: selectedCompany?.id,
              projectId: selectedProject?.id
            },
            data: {
              subject,
              name: "Sphere-Dashboard",
              comment: customMessage
            }
          }
        });
        openToast({
          title: "Your request has been received.",
          message: "Thank you for reaching out, we appreciate your feedback."
        });
      } catch (error_0) {
        handleErrorWithToast({
          id: `sendFeedback-${Date.now().toString()}`,
          title: "Error sending feedback",
          error: error_0
        });
      }
    }
    await Promise.all([sendFeedbackToFeedbackAPI(), sendFeedbackToCoreAPI()]);
  }
  return {
    sendFeedback
  };
}